class PredictiveSearch extends HTMLElement {
  static MOBILE_SEARCH_MEDIA = '(max-width: 1023.98px)';

  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.handleMobileSearchToggleClick = this.handleMobileSearchToggleClick.bind(this);

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  initHeaderContext() {
    this.header = this.closest('header');
    this.isHeaderSearch = this.header !== null;
    this.mobileSearchIconMode = this.header?.dataset.mobileSearchIcon === 'true';
  }

  connectedCallback() {
    this.initHeaderContext();
    this.setupMobileSearchToggle();
    if (!this.mobileSearchToggleBound && this.mobileSearchIconMode) {
      requestAnimationFrame(() => this.setupMobileSearchToggle());
    }
  }

  disconnectedCallback() {
    if (this.mobileSearchToggle && this.mobileSearchToggleBound) {
      this.mobileSearchToggle.removeEventListener('click', this.handleMobileSearchToggleClick);
      this.mobileSearchToggleBound = false;
    }
  }

  isMobileSearchDrawerActive() {
    return this.mobileSearchIconMode && window.matchMedia(PredictiveSearch.MOBILE_SEARCH_MEDIA).matches;
  }

  shouldOpenHeaderSearchOverlay() {
    return !this.searchRecommendationEmpty || this.isMobileSearchDrawerActive();
  }

  setupMobileSearchToggle() {
    if (!this.isHeaderSearch || !this.mobileSearchIconMode || this.mobileSearchToggleBound) return;

    this.mobileSearchToggle = this.header?.querySelector('.header-search-toggle');
    if (!this.mobileSearchToggle) return;

    this.mobileSearchToggle.addEventListener('click', this.handleMobileSearchToggleClick);
    this.mobileSearchToggleBound = true;
  }

  handleMobileSearchToggleClick(event) {
    if (!this.isMobileSearchDrawerActive()) return;

    event.preventDefault();
    event.stopPropagation();

    if (document.body.classList.contains(this.states.SEARCH_OPEN)) {
      this.toggleSearchState(false);
      return;
    }

    this.openMobileSearch();
  }

  openMobileSearch() {
    if (!this.input || !this.header || !this.isMobileSearchDrawerActive()) return;

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    document.body.classList.add(this.states.SEARCH_OPEN);
    this.classList.add(this.states.OPEN);
    this.toggleNavButtonVisibility(false);

    if (this.mobileSearchToggle) {
      this.mobileSearchToggle.setAttribute('aria-expanded', 'true');
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent?.classList.remove('hidden');
    }

    requestAnimationFrame(() => {
      this.input.focus({ preventScroll: true });
    });
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && this.shouldOpenHeaderSearchOverlay()) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (
          this.isHeaderSearch &&
          !document.body.classList.contains('search-open') &&
          this.shouldOpenHeaderSearchOverlay()
        ) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const isDesktopViewport = !window.matchMedia(PredictiveSearch.MOBILE_SEARCH_MEDIA).matches;
    const overlayCanClose =
      !this.mobileSearchIconMode || this.isMobileSearchDrawerActive() || isDesktopViewport;

    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        !target.closest('.header-search-toggle') &&
        ((target.classList.contains('fixed-overlay') &&
          target.closest('.header-section') &&
          overlayCanClose) ||
          (target.classList.contains('header__search-close') &&
            (!this.mobileSearchIconMode || this.isMobileSearchDrawerActive())))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    const open = isOpen ?? !this.classList.contains(this.states.OPEN);
    this.classList.toggle(this.states.OPEN, open);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, open);
      this.toggleNavButtonVisibility(!open);
      if (!this.mobileSearchToggle) {
        this.mobileSearchToggle = this.header?.querySelector('.header-search-toggle');
      }
      if (this.mobileSearchToggle && this.isMobileSearchDrawerActive()) {
        this.mobileSearchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    }
    if (!open && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
